module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "fm-title",
    attrs: {
      "id": "fm-3"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_1"
    }
  }, [_vm._v("Introduction: In Search of a New Methodological Vision for Constructive Theology")])]), _c('a', {
    attrs: {
      "id": "page_1"
    }
  }), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_1"
    }
  }, [_vm._v("As orientation to the current volume, this introduction briefly outlines the methodological vision of the project, presented and defended in earlier volumes."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft1",
      "id": "fn1"
    }
  }, [_vm._v("1")]), _vm._v(" The vision for doing constructive theology in a religiously pluralistic and culturally diverse “post-world”—postmodern, postfoundationalist, poststructuralist, postcolonial, postmetaphysical, postpropositional, postliberal, postconservative, postsecular, post-Christian—can be sketched like this:")]), _vm._v(" "), _c('p', {
    staticClass: "SmQuoteASb"
  }, [_vm._v("Systematic/constructive theology is an integrative discipline that continuously searches for a coherent, balanced understanding of Christian truth and faith in light of Christian tradition (biblical and historical) and in the context of the historical and contemporary thought, cultures, and living faiths. It aims at a coherent, inclusive, dialogical, and hospitable vision.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("The nomenclature “systematic” is most unfortunate since the ultimate goal of constructive theology is not a “system”! Rather, constructive theology seeks a coherent and balanced understanding. Regarding the theory of truth, it follows coherence theory. One current way of speaking of coherence compares it to a web or a net(work) that underwrites postfoundationalist rather than foundationalist epistemology. That metaphor is fitting, as it speaks of an "), _c('a', {
    attrs: {
      "id": "page_2"
    }
  }, [_vm._v("attempt to relate every statement to other relevant statements and ultimately to the “whole.” Coherence has not only to do with inner-textual coherence but also with the “fit” of theological statements with “reality.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft2",
      "id": "fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("With classical tradition (and say, Pannenberg, among contemporary theologians), in a qualified sense, we can still speak of systematic theology as the “science of God,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft3",
      "id": "fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" as it presupposes the existence of truth apart from human beings and human beings’ social construction thereof."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft4",
      "id": "fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" That said—without subscribing to any particular form of late modern/postmodern epistemology—we hasten to add that we humans never have a direct, uncontested access to the infinitely incomprehensible God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft5",
      "id": "fn5"
    }
  }, [_vm._v("5")])]), _vm._v(" Rather, human grasp of truth is only provisional."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft6",
      "id": "fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" Rather than on firm “foundations,” theological argumentation builds on a "), _c('em', [_vm._v("post")]), _vm._v("foundationalist epistemology because of “"), _c('em', [_vm._v("the contested nature of theological truth claims")]), _vm._v(".”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft7",
      "id": "fn7"
    }
  }, [_vm._v("7")])]), _vm._v(" A postfoundationalist approach or epistemology seeks “to engage in interdisciplinary dialogue within our postmodern culture while "), _c('em', [_vm._v("both")]), _vm._v(" maintaining a commitment to intersubjective, transcommunal theological argumentation for the truth of Christian faith, "), _c('em', [_vm._v("and")]), _vm._v(" recognizing the provisionality of our historically embedded understandings and culturally conditioned explanations of the Christian tradition and religious experience.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft8",
      "id": "fn8"
    }
  }, [_vm._v("8")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Constructive theology’s nature as an “integrative” discipline is its most distinctive feature. To practice well constructive theology, one has to utilize the results, insights, and materials of all other theological disciplines, that is, biblical studies, church history and historical theology, philosophical theology, as well as ministerial studies. Closely related fields of religious studies, ethics, and missiology also belong to the texture of systematic work. That alone is a tall order. But since theology operates in a particular context and religiously pluralistic world, the insights from cultural studies as well as the study of living faiths (in this project, Judaism, Islam, Buddhism, and Hinduism) are also invoked. Finally—alas!—even more resources are needed for the discussion of certain theological topics such as creation, anthropology, and eschatology—namely, the natural sciences, whether cosmology, physics, quantum theory, "), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("or brain study. The use of all these materials and insights, however, is guided by the principle according to which the systematician must listen carefully to related disciplines but also go beyond their input, domains, and questions. From the beginning to the end, this project is a "), _c('em', [_vm._v("theological")]), _vm._v(" enterprise.")])]), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("A methodological approach that “celebrates diversity, pluralism, and contextuality, while at the same time pursuing shared resources of human rationality and interdisciplinary conversation,”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft9",
      "id": "fn9"
    }
  }, [_vm._v("9")]), _vm._v(" is particularly appropriate in an investigation undertaken in the matrix of theology, religious studies, and the sciences. This kind of interdisciplinary discourse, “a complex, multileveled transversal process,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft10",
      "id": "fn10"
    }
  }, [_vm._v("10")])]), _vm._v(" breaks through the limitations of any specific discipline with standard borders. It seeks mutual learning, interaction, and engagement in its quest for a coherent vision. The term “transversal” indicates “a sense of extending over, lying across, and intersecting with one another.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft11",
      "id": "fn11"
    }
  }, [_vm._v("11")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("That principle, however, does not float freely in the air, as it were; it is not a smorgasbord where one can pick and choose as one wishes, to use another metaphor. Rather, it acknowledges the theory-laden nature of each inquiry but also the rootedness of such an inquiry in a particular tradition. For the theologian, the guiding tradition is the biblical-historical and contemporary theological wisdom, the deposit of faith. That tradition, however, is neither a straitjacket that limits creative pursuit of knowledge nor a basis for mere repetition and defense. Rather, remaining “tied to specific communities of faith without being trapped by these communities,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft12",
      "id": "fn12"
    }
  }, [_vm._v("12")])]), _vm._v(" the investigation honors contextuality and builds on a shared identity of the wider community of faith. Appreciation and critique of tradition are part of the task."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft13",
      "id": "fn13"
    }
  }, [_vm._v("13")])]), _vm._v(" Following Alasdair MacIntyre, it can be said that “the idea that there can be a kind of reason that is supra-cultural and that would enable us to view all the culturally conditioned traditions of rationality from a standpoint above them all is one of the illusions of our contemporary culture. All rationality is socially embodied, developed in human tradition and using some human language.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft14",
      "id": "fn14"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Particularly with regard to the comparative-theology facet of this investigation—namely, engaging Jewish, Islamic, Hindu, and Buddhist beliefs of "), _c('a', {
    attrs: {
      "id": "page_4"
    }
  }, [_vm._v("eschatology and ecclesiology—one has to be careful in following the transversality principle. The reasons are many and obvious. First of all, systematic theologians are not experts in religions—and even if we were, it would take a lifetime to learn even one tradition in any deep way. Therefore, we must do everything to avoid making “the systematic theologian into an amateur collector of religious curiosities.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft15",
      "id": "fn15"
    }
  }, [_vm._v("15")]), _vm._v(" The systematician should let the authoritative and representative voices of each tradition formulate their respective views. Second, granted the systematician knows enough to be able to dialogue meaningfully, integrating the contributions of religions in an already wide menu substantially complicates the task. Understandable is the temptation by modernistically driven theologians to give up the distinctive testimonies and “ground beliefs” of each tradition and in the name of the common core of religions try to make them speak (or at least mean) about the same. That “first-generation theological pluralism,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft16",
      "id": "fn16"
    }
  }, [_vm._v("16")])]), _vm._v(" however, is neither interesting nor useful. It also deviates from the principle of hospitality as it denies the right of the other to be "), _c('em', [_vm._v("other")]), _vm._v(". As argued with some detail in the methodological introduction to "), _c('em', [_vm._v("Christ and Reconciliation")]), _vm._v(" (vol. 1), not only is theology confessional (rightly understood), but so also is comparative theology. It is not confessional in terms of violence and oppression but rather in a way that makes room for distinctive identities, differing testimonies—and passionate search for a common understanding even in the midst of our deepest and most deeply held differences. Confessionalism is not a denial of the pluralistic nature of theology as a discipline.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The beginning of both "), _c('a', {
    attrs: {
      "href": "#/008_Part01#pt-1"
    }
  }, [_vm._v("parts 1")]), _vm._v(" and "), _c('span', {
    attrs: {
      "href": "#/019_Part02#pt-2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")]), _vm._v(" will provide a detailed orientation to the topics and flow of argumentation in the following chapters.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "images/foot.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn1",
      "id": "ft1"
    }
  }, [_vm._v("1")]), _vm._v(". This section repeats the materials from vol. 3, "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", which similarly engages robustly not only other faith traditions but also the natural sciences. Since the discussion in vol. 1, "), _c('em', [_vm._v("Christ and Reconciliation")]), _vm._v(", contains detailed bibliographic references, they are not repeated here, except for direct citations.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn2",
      "id": "ft2"
    }
  }, [_vm._v("2")]), _vm._v(". Murphy, "), _c('em', [_vm._v("Beyond Liberalism and Fundamentalism")]), _vm._v(", pp. 98–108.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn3",
      "id": "ft3"
    }
  }, [_vm._v("3")]), _vm._v(". Aquinas, "), _c('em', [_vm._v("ST")]), _vm._v(" 1a.1.7.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn4",
      "id": "ft4"
    }
  }, [_vm._v("4")]), _vm._v(". Pannenberg, "), _c('em', [_vm._v("ST")]), _vm._v(" 1:50.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn5",
      "id": "ft5"
    }
  }, [_vm._v("5")]), _vm._v(". Pannenberg, "), _c('em', [_vm._v("ST")]), _vm._v(" 1:4–6; for a full discussion, see his "), _c('em', [_vm._v("Theology and the Philosophy of Science")]), _vm._v(", pp. 297–326 particularly.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn6",
      "id": "ft6"
    }
  }, [_vm._v("6")]), _vm._v(". See further Pannenberg, “What Is Truth?” pp. 1–27; Vainio, "), _c('em', [_vm._v("Beyond Fideism")]), _vm._v(", p. 132.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn7",
      "id": "ft7"
    }
  }, [_vm._v("7")]), _vm._v(". Clayton, "), _c('em', [_vm._v("Adventures in the Spirit")]), _vm._v(", p. 28, emphasis in original (commenting on Pannenberg’s theology).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn8",
      "id": "ft8"
    }
  }, [_vm._v("8")]), _vm._v(". Shults, "), _c('em', [_vm._v("The Post Foundationalist Task of Theology")]), _vm._v(", p. 18, emphasis in original.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn9",
      "id": "ft9"
    }
  }, [_vm._v("9")]), _vm._v(". Van Huyssteen, "), _c('em', [_vm._v("Alone in the World?")]), _vm._v(" p. 5.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn10",
      "id": "ft10"
    }
  }, [_vm._v("10")]), _vm._v(". Van Huyssteen, "), _c('em', [_vm._v("Alone in the World?")]), _vm._v(" p. 9.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn11",
      "id": "ft11"
    }
  }, [_vm._v("11")]), _vm._v(". Van Huyssteen, "), _c('em', [_vm._v("Alone in the World?")]), _vm._v(" p. 20. See further Schrag, “Transversal Rationality.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn12",
      "id": "ft12"
    }
  }, [_vm._v("12")]), _vm._v(". Van Huyssteen, "), _c('em', [_vm._v("Alone in the World?")]), _vm._v(" p. 12.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn13",
      "id": "ft13"
    }
  }, [_vm._v("13")]), _vm._v(". See further Van Huyssteen, "), _c('em', [_vm._v("Alone in the World?")]), _vm._v(" p. 45 and passim.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn14",
      "id": "ft14"
    }
  }, [_vm._v("14")]), _vm._v(". As paraphrased by Newbigin, “Religious Pluralism,” p. 50; so also p. 52; the reference is to MacIntyre, "), _c('em', [_vm._v("Whose Justice, Which Rationality?")])]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn15",
      "id": "ft15"
    }
  }, [_vm._v("15")]), _vm._v(". K. Ward, "), _c('em', [_vm._v("Religion and Revelation")]), _vm._v(", p. 37.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn16",
      "id": "ft16"
    }
  }, [_vm._v("16")]), _vm._v(". See further my "), _c('em', [_vm._v("Trinity and Revelation")]), _vm._v(", chap. 14.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-5d064f99","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/007_Introduction.vue
// module id = 75
// module chunks = 0